/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview PaymentModal RegistrationStep step UI.
 * @module modals/PaymentModal/steps/PaymentModalRegistrationStep
 */

import Select from "react-select";
import CountryCodeSelector from "../../../components/CountryCodeSelector";
import { validateRFC, getSelectedOption, getProfileFieldValue, getSelectedCountryCodeOption } from "../../../utils/common.js";
import {
  getSelectControlStyles,
  getSelectContainerStyles,
  getSelectValueContainerStyles,
  getSelectInputStyles,
  getSelectOptionStyles,
  getSelectOptionStylesWithTextAlign,
  getSelectContainerStylesWithTextAlign,
  getSelectControlStylesWithTextOverflow,
  getSelectControlStylesWithTextWrap,
  hideIndicatorSeparator,
} from "../paymentModalSelectStyles.js";

const PaymentModalRegistrationStep = ({
  styles,
  t,
  event,
  formData,
  isFormDisabled,
  rfcFormatError,
  setRfcFormatError,
  handleInputChange,
  profileOptions,
  sectors,
  subSectors,
  branches,
  setFormData,
  setSubsectors,
  setBranches,
  operationEnvironmentOptions,
  handleAmbitoOperacionesChange,
  countryCodeOptions,
  handleFieldChange,
  getFieldValidationState,
  getErrorMessage,
  positionOptions,
  areaOptions,
  genderOptions,
}) => (
          <div className={styles.stepContent}>
            <div className={styles.scrollableContent}>
              <div className={styles.modalSubtitle}>
                {t("components.eventRegistrationModal.companyInfo")}
              </div>
              <div className={styles.generalInfoSection}>
                <div className={styles.generalInfoRow}>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.companyName")}
                    </label>
                    <input
                      type="text"
                      placeholder={t(
                        "components.eventRegistrationModal.companyNamePlaceholder",
                      )}
                      className={styles.input}
                      disabled={isFormDisabled}
                      style={{
                        borderColor: "#909092",
                      }}
                      value={formData.razonSocial || ""}
                      onChange={(e) =>
                        handleInputChange("razonSocial", e.target.value)
                      }
                    />
                  </div>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.rfc")}
                    </label>
                    <input
                      type="text"
                      placeholder={t(
                        "components.eventRegistrationModal.rfcPlaceholder",
                      )}
                      className={`${styles.input} ${
                        (formData.RFC || "").trim() !== "" && rfcFormatError
                          ? styles.inputError
                          : ""
                      }`}
                      disabled={isFormDisabled}
                      style={{
                        borderColor:
                          (formData.RFC || "").trim() !== "" && rfcFormatError
                            ? "#FF0000"
                            : "#909092",
                        textTransform: "uppercase",
                      }}
                      value={formData.RFC || ""}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        handleInputChange("RFC", value);
                        setRfcFormatError(null);
                      }}
                      onBlur={() => {
                        const rfcValue = formData.RFC?.trim() ?? "";
                        if (rfcValue === "") {
                          setRfcFormatError(null);
                          return;
                        }
                        const result = validateRFC(rfcValue);
                        setRfcFormatError(result.isValid ? null : result.error);
                      }}
                    />
                    {rfcFormatError && (
                      <p className={styles.errorMessage}>
                        {t("components.userCreationModal.rfcFormatError")}
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.generalInfoRow}>
                  <div className={styles.fullWidthContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.companyProfile")}
                    </label>
                    <Select
                      options={profileOptions}
                      isDisabled={isFormDisabled}
                      value={getSelectedOption(
                        profileOptions,
                        formData.perfilSeleccionado ?? 0,
                      )}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          handleInputChange(
                            "perfilSeleccionado",
                            selectedOption.value,
                          );
                        }
                      }}
                      placeholder={t("common.select")}
                      styles={{
                        control: getSelectControlStylesWithTextOverflow,
                        container: getSelectContainerStyles,
                        valueContainer: getSelectValueContainerStyles,
                        input: getSelectInputStyles,
                        option: getSelectOptionStylesWithTextAlign,
                        indicatorSeparator: hideIndicatorSeparator,
                      }}
                    />
                  </div>
                </div>
                <div className={styles.generalInfoRow}>
                  <div className={styles.fullWidthContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.sector")}
                    </label>
                    <Select
                      options={sectors}
                      isDisabled={isFormDisabled}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setFormData((prev) => ({
                            ...prev,
                            sector: selectedOption._id,
                            subSector: null,
                            rama: null,
                          }));
                          setSubsectors(selectedOption.subSectores ?? []);
                          setBranches([]);
                        }
                      }}
                      placeholder={t(
                        "components.eventRegistrationModal.sectorPlaceholder",
                      )}
                      styles={{
                        control: getSelectControlStylesWithTextOverflow,
                        container: getSelectContainerStyles,
                        valueContainer: getSelectValueContainerStyles,
                        input: getSelectInputStyles,
                        option: getSelectOptionStylesWithTextAlign,
                        indicatorSeparator: hideIndicatorSeparator,
                      }}
                      getOptionValue={(option) => option.nombre}
                      formatOptionLabel={(option) => (
                        <span>{option.nombre}</span>
                      )}
                    />
                  </div>
                </div>
                <div className={styles.generalInfoRow}>
                  <div className={styles.fullWidthContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.subsector")}
                    </label>
                    <Select
                      options={subSectors}
                      isSearchable={false}
                      isDisabled={isFormDisabled || !formData.sector}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setFormData((prev) => ({
                            ...prev,
                            subSector: selectedOption._id,
                            rama: null,
                          }));
                          setBranches(selectedOption.ramas ?? []);
                        }
                      }}
                      placeholder={t("pages.eventRegistrationModal.subsectorPlaceholder")}
                      styles={{
                        control: getSelectControlStylesWithTextOverflow,
                        container: getSelectContainerStylesWithTextAlign,
                        valueContainer: getSelectValueContainerStyles,
                        input: getSelectInputStyles,
                        option: getSelectOptionStyles,
                        indicatorSeparator: hideIndicatorSeparator,
                      }}
                      getOptionValue={(option) => option.nombre}
                      formatOptionLabel={(option) => (
                        <span>{option.nombre}</span>
                      )}
                    />
                  </div>
                </div>
                <div className={styles.generalInfoRow}>
                  <div className={styles.fullWidthContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.branch")}
                    </label>
                    <Select
                      options={branches}
                      isDisabled={isFormDisabled || !formData.subSector}
                      isSearchable={false}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          handleInputChange("rama", selectedOption._id);
                        }
                      }}
                      placeholder={t("pages.eventRegistrationModal.branchPlaceholder")}
                      styles={{
                        control: getSelectControlStylesWithTextWrap,
                        container: getSelectContainerStylesWithTextAlign,
                        valueContainer: getSelectValueContainerStyles,
                        input: getSelectInputStyles,
                        option: getSelectOptionStyles,
                        indicatorSeparator: hideIndicatorSeparator,
                      }}
                      getOptionValue={(option) => option.nombre}
                      formatOptionLabel={(option) => (
                        <span>{option.nombre}</span>
                      )}
                    />
                  </div>
                </div>
                <div className={styles.generalInfoRow}>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.totalWorkers")}
                    </label>
                    <input
                      type="number"
                      placeholder={t(
                        "components.eventRegistrationModal.totalWorkersPlaceholder",
                      )}
                      className={`${styles.input} ${styles.numberInput} ${
                        getFieldValidationState(
                          "numeroTotalTrabajadores",
                          formData.numeroTotalTrabajadores || "",
                        ) === "error"
                          ? styles.inputError
                          : ""
                      }`}
                      disabled={isFormDisabled}
                      style={{
                        borderColor:
                          getFieldValidationState(
                            "numeroTotalTrabajadores",
                            formData.numeroTotalTrabajadores || "",
                          ) === "error"
                            ? "#FF0000"
                            : "#909092",
                      }}
                      value={formData.numeroTotalTrabajadores || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow digits
                        if (value === "" || /^\d+$/.test(value)) {
                          handleInputChange("numeroTotalTrabajadores", value);
                          handleFieldChange("numeroTotalTrabajadores", value);
                        }
                      }}
                      min="0"
                      step="1"
                    />
                    {getErrorMessage("numeroTotalTrabajadores") && (
                      <p className={styles.errorMessage}>
                        {getErrorMessage("numeroTotalTrabajadores")}
                      </p>
                    )}
                  </div>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.scope")}
                    </label>
                    <Select
                      value={getSelectedOption(
                        operationEnvironmentOptions,
                        getProfileFieldValue(
                          formData,
                          "ambitoOperaciones",
                          formData.perfilSeleccionado ?? 0,
                          formData.ambitoOperaciones,
                        ),
                      )}
                      isSearchable={false}
                      options={operationEnvironmentOptions}
                      isDisabled={isFormDisabled}
                      onChange={handleAmbitoOperacionesChange}
                      placeholder={t(
                        "components.eventRegistrationModal.scopePlaceholder",
                      )}
                      styles={{
                        control: getSelectControlStyles,
                        container: getSelectContainerStyles,
                        valueContainer: (baseStyles) => ({
                          ...baseStyles,
                          maxWidth: "120px",
                        }),
                        input: getSelectInputStyles,
                        option: getSelectOptionStyles,
                        indicatorSeparator: hideIndicatorSeparator,
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.modalSubtitle}>
                {t("components.eventRegistrationModal.personalInformation")}
              </div>
              <div className={styles.generalInfoSection}>
                <div className={styles.generalInfoRow}>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.name")}
                    </label>
                    <input
                      type="text"
                      placeholder={t(
                        "components.eventRegistrationModal.namePlaceholder",
                      )}
                      className={`${styles.input} ${
                        getFieldValidationState(
                          "nombre",
                          formData.nombre || "",
                        ) === "error"
                          ? styles.inputError
                          : ""
                      }`}
                      disabled={isFormDisabled}
                      style={{
                        borderColor:
                          getFieldValidationState(
                            "nombre",
                            formData.nombre || "",
                          ) === "error"
                            ? "#FF0000"
                            : "#909092",
                      }}
                      value={formData.nombre || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange("nombre", value);
                        handleFieldChange("nombre", value);
                      }}
                      maxLength={100}
                    />
                    {getErrorMessage("nombre") && (
                      <p className={styles.errorMessage}>
                        {getErrorMessage("nombre")}
                      </p>
                    )}
                  </div>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.phone")}
                    </label>
                    <div className={styles.phoneInputContainer}>
                      <CountryCodeSelector
                        value={getSelectedCountryCodeOption(
                          countryCodeOptions,
                          formData.codiogPais,
                        )}
                        onChange={(value) => {
                          handleInputChange("codiogPais", value.value);
                        }}
                        isDisabled={isFormDisabled}
                      />
                      <input
                        type="text"
                        className={`${styles.phoneInput} ${
                          getFieldValidationState(
                            "telefono",
                            formData.telefono || "",
                          ) === "error"
                            ? styles.inputError
                            : ""
                        }`}
                        disabled={isFormDisabled}
                        placeholder={t(
                          "components.contactInformation.officePhonePlaceholder",
                        )}
                        value={formData.telefono || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleInputChange("telefono", value);
                          handleFieldChange("telefono", value);
                        }}
                        style={{
                          borderColor:
                            getFieldValidationState(
                              "telefono",
                              formData.telefono || "",
                            ) === "error"
                              ? "#FF0000"
                              : "#909092",
                        }}
                      />
                    </div>
                    {getErrorMessage("telefono") && (
                      <p className={styles.errorMessage}>
                        {getErrorMessage("telefono")}
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.generalInfoRow}>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.email")}
                    </label>
                    <input
                      type="text"
                      placeholder={t(
                        "components.eventRegistrationModal.emailPlaceholder",
                      )}
                      className={`${styles.input} ${
                        getFieldValidationState(
                          "correoElectronico",
                          formData.correoElectronico || "",
                        ) === "error"
                          ? styles.inputError
                          : ""
                      }`}
                      disabled={isFormDisabled}
                      style={{
                        borderColor:
                          getFieldValidationState(
                            "correoElectronico",
                            formData.correoElectronico || "",
                          ) === "error"
                            ? "#FF0000"
                            : "#909092",
                      }}
                      value={formData.correoElectronico || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange("correoElectronico", value);
                        handleFieldChange("correoElectronico", value);
                      }}
                      maxLength={100}
                    />
                    {getErrorMessage("correoElectronico") && (
                      <p className={styles.errorMessage}>
                        {getErrorMessage("correoElectronico")}
                      </p>
                    )}
                  </div>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.position")}
                    </label>
                    <Select
                      value={getSelectedOption(
                        positionOptions,
                        formData.puesto,
                      )}
                      isSearchable={false}
                      options={positionOptions}
                      isDisabled={isFormDisabled}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          handleInputChange("puesto", selectedOption.value);
                        }
                      }}
                      placeholder={t(
                        "components.eventRegistrationModal.positionPlaceholder",
                      )}
                      styles={{
                        control: getSelectControlStyles,
                        container: getSelectContainerStyles,
                        valueContainer: (baseStyles) => ({
                          ...baseStyles,
                          maxWidth: "120px",
                        }),
                        input: getSelectInputStyles,
                        option: getSelectOptionStyles,
                        indicatorSeparator: hideIndicatorSeparator,
                      }}
                    />
                  </div>
                </div>
                <div className={styles.generalInfoRow}>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.area")}
                    </label>
                    <Select
                      value={getSelectedOption(areaOptions, formData.area)}
                      options={areaOptions}
                      isDisabled={isFormDisabled}
                      placeholder={t(
                        "components.eventRegistrationModal.areaPlaceholder",
                      )}
                      styles={{
                        control: getSelectControlStyles,
                        container: getSelectContainerStyles,
                        valueContainer: (baseStyles) => ({
                          ...baseStyles,
                          maxWidth: "120px",
                        }),
                        input: getSelectInputStyles,
                        option: getSelectOptionStyles,
                        indicatorSeparator: hideIndicatorSeparator,
                      }}
                      className="select__control"
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          handleInputChange("area", selectedOption.value);
                        }
                      }}
                    />
                  </div>
                  <div className={styles.categoryContainer}>
                    <label className={styles.label}>
                      {t("components.eventRegistrationModal.gender")}
                    </label>
                    <Select
                      value={getSelectedOption(genderOptions, formData.genero)}
                      isSearchable={false}
                      options={genderOptions}
                      isDisabled={isFormDisabled}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          handleInputChange("genero", selectedOption.value);
                        }
                      }}
                      placeholder={t(
                        "components.eventRegistrationModal.genderPlaceholder",
                      )}
                      styles={{
                        control: getSelectControlStyles,
                        container: getSelectContainerStyles,
                        valueContainer: (baseStyles) => ({
                          ...baseStyles,
                          maxWidth: "120px",
                        }),
                        input: getSelectInputStyles,
                        option: getSelectOptionStyles,
                        indicatorSeparator: hideIndicatorSeparator,
                      }}
                    />
                  </div>
                </div>
              </div>
              {(event?.interesesComercialesRequerido ||
                event?.expectativasRequerido) && (
                <>
                  <div className={styles.modalSubtitle}>
                    {t("components.eventRegistrationModal.additional")}
                  </div>
                  <div className={styles.optionalRequirementsSection}>
                    {event?.interesesComercialesRequerido && (
                      <div className={styles.generalInfoRow}>
                        <div className={styles.fullWidthContainer}>
                          <label className={styles.label}>
                            {t("components.eventRegistrationModal.interests")}
                          </label>
                          <input
                            type="text"
                            placeholder={t(
                              "components.eventRegistrationModal.interestsPlaceholder",
                            )}
                            className={styles.input}
                            disabled={isFormDisabled}
                            value={formData.interesesComerciales || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleInputChange("interesesComerciales", value);
                            }}
                            maxLength={255}
                          />
                          <span className={styles.requiredMessage}>
                            {t(
                              "components.eventRegistrationModal.requiredMessage",
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                    {event?.expectativasRequerido && (
                      <div className={styles.generalInfoRow}>
                        <div className={styles.fullWidthContainer}>
                          <label className={styles.label}>
                            {t("components.eventRegistrationModal.question")}
                          </label>
                          <textarea
                            placeholder={t(
                              "components.eventRegistrationModal.questionPlaceholder",
                            )}
                            className={styles.textarea}
                            disabled={isFormDisabled}
                            value={formData.expectativas || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleInputChange("expectativas", value);
                            }}
                            maxLength={300}
                          />
                          <span className={styles.requiredMessage}>
                            {t(
                              "components.eventRegistrationModal.requiredMessage",
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
);

export default PaymentModalRegistrationStep;
